import { createRouter, createWebHistory } from '@ionic/vue-router';
// @ts-ignore - Type import from vue-router causes conflicts with Ionic Vue Router
import type { RouteRecordRaw } from 'vue-router';
import accountRoutes, { discardAbandonedBundleEdit } from './accountRoutes';
import locationRoutes from './locationRoutes';
import staticPageRoutes from './staticPageRoutes';
import menuRoutes from './menuRoutes';
import { vanillaQueryParam } from '@/services/pageService';
import cateringRoutes from './cateringRoutes';
import { PERSIST_TRACKING_PARAMS } from '@/constants';
import { setupRouterSEO } from '@/plugins/seo/router';
import store from '../store';

// ! Remember to update baseSitemap.json if adding new routes
const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        component: () => import('@/views/Home.vue'),
        meta: {
            seo: {
                title: 'Donatos Pizza',
                description: 'Order Online or Find a Location Near You.',
                keywords: ['Donatos Pizza', 'Donatos Menu', 'Donatos Delivery', 'Donatos near me', 'order online', 'pizza order online', 'pizza near me', 'order pizza online'],
                ogType: 'website',
                ogImage: '/images/donatos-og-home.jpg',
                structuredData: {
                    '@type': 'Restaurant',
                    name: 'Donatos Pizza',
                    url: 'https://donatos.com',
                    servesCuisine: 'Pizza',
                    telephone: '1-800-DONATOS',
                },
            },
        },
    },
    {
        path: '/index.html',
        redirect: '/',
    },
    {
        path: '/404',
        component: () => import('@/views/404.vue'),
        meta: {
            seo: {
                title: 'Page Not Found - Donatos Pizza',
                description: "The page you're looking for doesn't exist. Visit our home page to order delicious pizza.",
                robots: 'noindex, nofollow',
            },
        },
    },
    {
        path: '/migrate-social',
        name: 'MigrateSocial',
        component: () => import('@/views/account/migrateSocialPage.vue'),
        meta: {
            requiresAuth: true,
            authRedirect: '/login?redirect=/migrate-social',
        },
    },
    ...accountRoutes,
    ...locationRoutes,
    ...staticPageRoutes,
    ...menuRoutes,
    ...cateringRoutes,
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

const checkAuthStatus = async () => {
    await store.dispatch('waitForAuthReady');
    const isAuthenticated = store.getters.isLoggedIn;
    return isAuthenticated;
};

const checkForRequiredLocation = async () => {
    await store.dispatch('waitForLocationReady');
    const location = store.getters.getSelectedLocation;
    return !!location;
};

// Setup SEO functionality for router
setupRouterSEO(router);
/**
 * Initial tracking params (utm_source, utm_medium, gclid, etc) from ads can be applied
 * External params are collected here
 */
const initialParams: any[] = [];
PERSIST_TRACKING_PARAMS.filter((q) => vanillaQueryParam(q)).forEach((q) => initialParams.push({ [q]: vanillaQueryParam(q) }));

router.beforeEach(async (to, from, next) => {
    // Runs ahead of the meta checks below so it still fires on routes that short-circuit them.
    discardAbandonedBundleEdit(to);

    // Handle tracking params persistence
    const addBackInitialQueryParams: any = to.query;
    initialParams.forEach((p) => {
        const [key, initialParamValue] = Object.entries(p)[0];

        // Check vue query param vs the initial value, if different => use the initial to update next()
        if (to.query[key] != initialParamValue) {
            addBackInitialQueryParams[key] = initialParamValue;
        } else {
            addBackInitialQueryParams[key] = to.query[key];
        }
    });

    // Remove any existing canonical tag (will be handled by SEO plugin)
    const existingLinkTag = document.querySelector('link[rel="canonical"]');

    if (existingLinkTag) {
        existingLinkTag.remove();
    }

    const meta = to.meta;
    if (!meta) {
        return next();
    }
    if (meta.requiresAuth) {
        const isAuthorized = await checkAuthStatus();
        if (!isAuthorized) {
            return meta.authRedirect ? next(meta.authRedirect) : next('/login');
        }
    }
    if (meta.requiresLocation) {
        const hasLocation = await checkForRequiredLocation();
        if (!hasLocation) {
            return next('/location');
        }
    }
    if (meta.awaitLocation) {
        await store.dispatch('waitForLocationReady');
    }
    return next();
});

router.afterEach(() => {
    const linkTag = document.createElement('link');
    linkTag.setAttribute('rel', 'canonical');
    linkTag.href = window.location.href;
    document.head.appendChild(linkTag);
});

export default router;
